import React, { useEffect } from "react";
import { Box, List, ListItem, ListItemText, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { NAV } from "../Navbar/navbarTheme";
import ContactChat from "../Hellper/ContactChat";
import aboutHeadingImg from "../Images/about/Add a heading.png";
import exportLogisticsImg from "../Images/about/Export & Logistics Support.png";

const introParagraphs = [
  "UK TO MARS LTD is a fast-growing UK-based wholesaler and exporter of British and selected European FMCG products, supplying customers across local and international markets with trusted brands at competitive wholesale prices.",
  "From food and drinks, confectionery and grocery to household, personal care, baby care, frozen foods, pet care, and specialist product ranges, we supply a wide selection of fast-moving consumer goods tailored to wholesalers, distributors, supermarkets, convenience stores, and retail chains.",
  "With a growing global customer network and strong supplier relationships across the UK and Europe, we focus on providing reliable supply, flexible wholesale solutions, competitive pricing, and efficient customer support designed around modern trade and export requirements.",
  "Whether customers require individual case quantities, mixed pallets, full pallets, truckloads, or container shipments, our team manages the complete supply process — from sourcing and consolidation to logistics coordination and export documentation.",
  "With access to a broad supplier network, we can also source many products requested by customers, including items not currently listed on our website.",
];

const aboutSections = [
  {
    title: "Export & Logistics Support",
    image: exportLogisticsImg,
    imageAlt: "Export and logistics support",
    paragraphs: [
      "We assist customers with product sourcing, order consolidation, loading coordination, logistics, and export documentation — helping simplify the buying and shipping process from the UK.",
      "Our team works with trusted logistics partners to handle local deliveries and international shipments by road, sea, and air, depending on customer requirements.",
      "We also support customers with:",
    ],
    bullets: [
      "Mixed product consolidation",
      "Palletised and container shipments",
      "Multi-product loading coordination",
      "Labelling and packaging",
      "Export documentation",
    ],
  },
];

const missionParagraphs = [
  "Our mission is to provide reliable, flexible, and competitive FMCG wholesale and export solutions that help businesses source trusted products efficiently from the UK and Europe.",
  "We are committed to supporting our customers through dependable supply, responsive service, and long-term business partnerships across local and international markets.",
];

const pageContentSx = {
  px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
  py: { xs: 3.5, md: 5 },
  maxWidth: 1240,
  mx: "auto",
  width: "100%",
  boxSizing: "border-box",
};

const listItemSx = {
  padding: 0,
  display: "list-item",
  pl: 2.5,
  mb: 0.5,
};

const bulletListSx = {
  color: NAV.ink,
  mb: 0,
  pl: { xs: 2, sm: 2.5 },
  listStyleType: "disc",
};

const sectionHeadingSx = {
  color: NAV.ink,
  fontSize: { xs: "1.25rem", md: "1.5rem" },
  fontWeight: 700,
  lineHeight: 1.3,
  mb: { xs: 1.75, md: 2 },
};

const sectionBodySx = {
  color: NAV.ink,
  fontSize: { xs: "0.9375rem", md: "1.0625rem" },
  lineHeight: 1.7,
  mb: 1.5,
};

function AboutBulletList({ items }) {
  if (!items?.length) return null;
  return (
    <List dense sx={bulletListSx}>
      {items.map((item) => (
        <ListItem key={item} sx={listItemSx}>
          <ListItemText
            primary={item}
            primaryTypographyProps={{
              sx: {
                fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                lineHeight: 1.7,
              },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

function AboutSection({ section, index, imageLeft }) {
  const { title, image, imageAlt, paragraphs, bullets } = section;

  return (
    <Box
      component="section"
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          md: imageLeft ? "row-reverse" : "row",
        },
        alignItems: { xs: "stretch", md: "flex-start" },
        gap: { xs: 3, md: 5, lg: 6 },
        py: { xs: 3.5, md: 4.5, lg: 5 },
        bgcolor: "#fff",
        borderTop: index > 0 ? `1px solid ${NAV.line}` : "none",
      }}
    >
      <Box
        sx={{
          flex: { md: "1 1 55%" },
          minWidth: 0,
          pr: { md: imageLeft ? 0 : 1, lg: 2 },
          pl: { md: imageLeft ? 1 : 0, lg: imageLeft ? 2 : 0 },
        }}
      >
        <Typography component="h2" sx={sectionHeadingSx}>
          {title}
        </Typography>
        {paragraphs.map((text) => (
          <Typography
            key={text}
            sx={{
              ...sectionBodySx,
              fontWeight: text.endsWith(":") ? 600 : 400,
            }}
          >
            {text}
          </Typography>
        ))}
        <AboutBulletList items={bullets} />
      </Box>

      <Box
        sx={{
          flex: { md: "1 1 45%" },
          minWidth: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: { xs: "center", md: "flex-start" },
        }}
      >
        <Box
          component="img"
          src={image}
          alt={imageAlt}
          sx={{
            display: "block",
            width: { xs: "92%", sm: "84%", md: "100%" },
            height: "auto",
            maxWidth: { xs: 320, sm: 360, md: 460, lg: 500 },
            maxHeight: { md: 400, lg: 440 },
            mx: { xs: "auto", md: 0 },
            objectFit: "contain",
            objectPosition: "center center",
          }}
        />
      </Box>
    </Box>
  );
}

function AboutUs() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box component="main" sx={{ width: "100%" }}>
      <Box
        component="header"
        sx={{
          width: "100%",
          bgcolor: "#eef5fb",
          borderBottom: `1px solid ${NAV.line}`,
        }}
      >
        <Box
          component="img"
          src={aboutHeadingImg}
          alt="About Us — Independent UK FMCG Wholesaler & Exporter"
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
            objectFit: "contain",
            objectPosition: "center top",
          }}
        />
      </Box>

      <Box sx={pageContentSx}>
        <Box
          component="section"
          aria-label="Introduction"
          sx={{
            mb: { xs: 3, md: 4 },
            pb: { xs: 3, md: 4 },
            borderBottom: `1px solid ${NAV.line}`,
          }}
        >
          <Typography component="h1" sx={sectionHeadingSx}>
            Independent UK FMCG Wholesaler &amp; Exporter
          </Typography>
          {introParagraphs.map((text, index) => (
            <Typography
              key={text}
              sx={{
                ...sectionBodySx,
                mb: index < introParagraphs.length - 1 ? 2 : 0,
                maxWidth: 900,
              }}
            >
              {text}
            </Typography>
          ))}
        </Box>

        {aboutSections.map((section, index) => (
          <AboutSection
            key={section.title}
            section={section}
            index={index}
            imageLeft={index % 2 === 1}
          />
        ))}

        <Box
          component="section"
          sx={{
            py: { xs: 3.5, md: 4.5, lg: 5 },
            borderTop: `1px solid ${NAV.line}`,
          }}
        >
          <Typography component="h2" sx={sectionHeadingSx}>
            Our Mission
          </Typography>
          {missionParagraphs.map((text, index) => (
            <Typography
              key={text}
              sx={{
                ...sectionBodySx,
                mb: index < missionParagraphs.length - 1 ? 2 : 0,
                maxWidth: 900,
              }}
            >
              {text}
            </Typography>
          ))}
        </Box>
      </Box>

      <ContactChat />
    </Box>
  );
}

export default AboutUs;
